<template>
<div>

  <tti-header class="shadow-sm"></tti-header>
  
  <div class="py-10 bg-gray-100">
    <main class="mx-auto py-10 px-4 max-w-2xl bg-white shadow sm:rounded-lg sm:px-10">

      <div v-if="loading" class="space-y-5">
        <div class="mx-auto w-24 h-24 bg-gray-200 rounded-full animate-pulse"></div>
        <div v-for="i of 3" :key="i" class="space-y-1">
          <div class="w-36 h-5 bg-gray-200 rounded animate-pulse"></div>
          <div class="w-60 h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div class="mx-auto w-36 h-12 bg-gray-200 rounded animate-pulse"></div>
      </div>

      <div v-else-if="notFound" class="text-center">
        <p class="mb-1 text-gray-800">
          We were unable to find this reference request.
        </p>
        <router-link to="/" class="text-blue-500 font-medium hover:underline">Return to Home</router-link>
      </div>

      <div v-else-if="submitted" class="text-center">
        <p class="mb-1 text-gray-800">
          Thank you for your submission.
        </p>
        <router-link to="/" class="text-blue-500 font-medium hover:underline">Return to Home</router-link>
      </div>

      <template v-else>
        <img v-if="tutor.Photo" :src="tutor.Photo" class="mx-auto w-24 h-24 rounded-full" />
        <span v-else class="relative block mx-auto h-24 w-24 rounded-full overflow-hidden bg-gray-100">
          <svg class="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </span>

        <h1 class="mt-2 text-gray-700 font-semibold text-center">
          Reference request for
          <span class="block text-xl">
            {{ tutor.Name }}
          </span>
        </h1>

        <p class="mt-6 text-sm text-gray-800">
          {{ tutor.Name }} has requested you provide a reference for them.
          This is a safeguarding measure we require of all our tutors, and helps us conform to government legislation.
          Please note: references <strong>cannot</strong> be submitted by family members.
        </p>

        <p class="mt-3 text-sm text-gray-800">
          Take your time to complete the following questions, accurately and honestly, to the best of your knowledge.
          <span class="font-bold">Your answers and comments will be kept strictly confidential, and will <strong>not</strong> be shared with {{ tutor.Name }}.</span>
        </p>

        <p class="mt-3 text-sm text-gray-800">
          If you do not wish to provide a reference, you may <button type="button" @click="submitForm(true)" class="text-blue-600 font-medium">decline the request</button>.
          We will not ask for a reason and we will not inform {{ tutor.Name }} of your decision.
        </p>

        <hr class="my-6" />
        
        <form
          @submit.prevent="submitForm(false)"
          class="space-y-8"
        >

          <div>
            <label for="refereeName" class="block text-sm font-medium text-gray-700">Your Name</label>
            <input v-model="form.RefereeName" id="refereeName" name="refereeName" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:w-1/2 sm:text-sm placeholder-gray-400" />
            <p v-if="$v.form.RefereeName.$dirty && !$v.form.RefereeName.required" class="mt-2 text-sm text-red-600">Please enter your name.</p>
          </div>


          <div>
            <label for="relationship" class="block text-sm font-medium text-gray-700">What is your relationship to {{ tutor.Name }}?</label>
            <input v-model="form.Relationship" id="relationship" name="relationship" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:w-1/2 sm:text-sm placeholder-gray-400" />
            <p v-if="$v.form.Relationship.$dirty && !$v.form.Relationship.required" class="mt-2 text-sm text-red-600">Please indicate your relationship to {{ tutor.Name }}.</p>
          </div>


          <div>
            <label for="relationshipLength" class="block text-sm font-medium text-gray-700">How long have you known {{ tutor.Name }}?</label>
            <select v-model="form.RelationshipLength" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:w-1/2 sm:text-sm placeholder-gray-400">
              <option>0-2 years</option>
              <option>2-5 years</option>
              <option>5-10 years</option>
              <option>10+ years</option>
            </select>
            <p v-if="$v.form.RelationshipLength.$dirty && !$v.form.RelationshipLength.required" class="mt-2 text-sm text-red-600">Please specify how long you have known {{ tutor.Name }}.</p>
          </div>

          
          <div>
            <label for="UnsuitableToTutor" class="block text-sm font-medium text-gray-700">
              With reference to the below description of duties and responsibilities, do you have any reason to believe {{ tutor.Name }} would be unsuitable to tutor?
            </label>
            <ul class="my-2 pl-5 space-y-1 list-disc text-sm text-gray-800">
              <li>Able to form good professional relationships with adults and children.</li>
              <li>Appropriate qualifications or an in-depth knowledge and substantial experience in their subject specialism(s).</li>
              <li>An awareness of different learning strategies and teaching methods suitable for teaching a diverse range of abilities.</li>
              <li>Highly organised, able to manage their own time, plan and deliver lessons.</li>
              <li>Effective oral and written communication, and interpersonal skills to communicate effectively with both adult and child learners.</li>
            </ul>
            <TailwindButtonGroup :value="form.UnsuitableToTutor" @click="form.UnsuitableToTutor = $event" :options="[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]" class="mt-1" />
            <p v-if="$v.form.UnsuitableToTutor.$dirty && !$v.form.UnsuitableToTutor.required" class="mt-2 text-sm text-red-600">Please choose an answer.</p>

            <div v-if="form.UnsuitableToTutor === 'Yes'" class="mt-3 pt-4 pb-1 border-l-4 border-gray-400 pl-4">
              <label for="UnsuitableToTutorDesc" class="block text-sm font-medium text-gray-700">
                Please provide specific reasons for your concerns.
              </label>
              <textarea
                v-model="form.UnsuitableToTutorDesc"
                class="mt-1 block w-full h-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400"
              />
              <p v-if="$v.form.UnsuitableToTutorDesc.$dirty && !$v.form.UnsuitableToTutorDesc.required" class="mt-2 text-sm text-red-600">Please provide some details.</p>
            </div>
          </div>


          <div>
            <label for="SuitableWorkWithChildren" class="block text-sm font-medium text-gray-700">
              Do you have any reason to believe {{ tutor.Name }} may be unsuitable to work with children or vulnerable adults?
            </label>
            <TailwindButtonGroup :value="form.SuitableWorkWithChildren" @click="form.SuitableWorkWithChildren = $event" :options="[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]" class="mt-1" />
            <p v-if="$v.form.SuitableWorkWithChildren.$dirty && !$v.form.SuitableWorkWithChildren.required" class="mt-2 text-sm text-red-600">Please choose an answer.</p>

            <div v-if="form.SuitableWorkWithChildren === 'Yes'" class="mt-3 pt-4 pb-1 border-l-4 border-gray-400 pl-4">
              <label for="SuitableWorkWithChildrenDesc" class="block text-sm font-medium text-gray-700">
                Please provide specific reasons for your concerns.
              </label>
              <textarea
                v-model="form.SuitableWorkWithChildrenDesc"
                class="mt-1 block w-full h-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400"
              />
              <p v-if="$v.form.SuitableWorkWithChildrenDesc.$dirty && !$v.form.SuitableWorkWithChildrenDesc.required" class="mt-2 text-sm text-red-600">Please provide some details.</p>
            </div>
          </div>


          <div>
            <label for="NotInvolvedInExtremism" class="block text-sm font-medium text-gray-700">
              Has {{ tutor.Name }} ever being involved in “extremism”, being vocal or active opposition to fundamental British values, including democracy, the rule of law, individual liberty and mutual respect and tolerance of different faiths and beliefs? Extremism also includes calls for the death of members of our armed forces, whether in this country or overseas.
            </label>
            <TailwindButtonGroup :value="form.NotInvolvedInExtremism" @click="form.NotInvolvedInExtremism = $event" :options="[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]" class="mt-1" />
            <p v-if="$v.form.NotInvolvedInExtremism.$dirty && !$v.form.NotInvolvedInExtremism.required" class="mt-2 text-sm text-red-600">Please choose an answer.</p>

            <div v-if="form.NotInvolvedInExtremism === 'Yes'" class="mt-3 pt-4 pb-1 border-l-4 border-gray-400 pl-4">
              <label for="NotInvolvedInExtremismDesc" class="block text-sm font-medium text-gray-700">
                Please provide further details.
              </label>
              <textarea
                v-model="form.NotInvolvedInExtremismDesc"
                class="mt-1 block w-full h-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400"
              />
              <p v-if="$v.form.NotInvolvedInExtremismDesc.$dirty && !$v.form.NotInvolvedInExtremismDesc.required" class="mt-2 text-sm text-red-600">Please provide some details.</p>
            </div>
          </div>


          <div>
            <label for="NeverSanctioned" class="block text-sm font-medium text-gray-700">
              Has {{ tutor.Name }} ever been referred to, or been the subject of a sanction, restriction or prohibition issued by the Teaching Regulation Agency (previously known as NCTL - the National College for Teaching and Leadership), any equivalent body in the UK or a regulator of the teaching profession in any other country?
            </label>
            <TailwindButtonGroup :value="form.NeverSanctioned" @click="form.NeverSanctioned = $event" :options="[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]" class="mt-1" />
            <p v-if="$v.form.NeverSanctioned.$dirty && !$v.form.NeverSanctioned.required" class="mt-2 text-sm text-red-600">Please choose an answer.</p>

            <div v-if="form.NeverSanctioned === 'Yes'" class="mt-3 pt-4 pb-1 border-l-4 border-gray-400 pl-4">
              <label for="NeverSanctionedDesc" class="block text-sm font-medium text-gray-700">
                Please provide further details.
              </label>
              <textarea
                v-model="form.NeverSanctionedDesc"
                class="mt-1 block w-full h-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400"
              />
              <p v-if="$v.form.NeverSanctionedDesc.$dirty && !$v.form.NeverSanctionedDesc.required" class="mt-2 text-sm text-red-600">Please provide some details.</p>
            </div>
          </div>


          <div>
            <label for="NeverReferredToDfE" class="block text-sm font-medium text-gray-700">
              Has {{ tutor.Name }} ever been referred to the Department for Education, or are they the subject of a direction under section 128 of the Education and Skills Act 2008 which prohibits, disqualifies or restricts them from being involved in the management of an independent school?
            </label>
            <TailwindButtonGroup :value="form.NeverReferredToDfE" @click="form.NeverReferredToDfE = $event" :options="[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]" class="mt-1" />
            <p v-if="$v.form.NeverReferredToDfE.$dirty && !$v.form.NeverReferredToDfE.required" class="mt-2 text-sm text-red-600">Please choose an answer.</p>

            <div v-if="form.NeverReferredToDfE === 'Yes'" class="mt-3 pt-4 pb-1 border-l-4 border-gray-400 pl-4">
              <label for="NeverSanctionedDesc" class="block text-sm font-medium text-gray-700">
                Please provide further details.
              </label>
              <textarea
                v-model="form.NeverReferredToDfEDesc"
                class="mt-1 block w-full h-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400"
              />
              <p v-if="$v.form.NeverReferredToDfEDesc.$dirty && !$v.form.NeverReferredToDfEDesc.required" class="mt-2 text-sm text-red-600">Please provide some details.</p>
            </div>
          </div>


          <div>
            <label for="NeverSection142" class="block text-sm font-medium text-gray-700">
              Has {{ tutor.Name }} ever been the subject of a direction under section 142 of the Education Act 2002?
            </label>
            <TailwindButtonGroup :value="form.NeverSection142" @click="form.NeverSection142 = $event" :options="[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]" class="mt-1" />
            <p v-if="$v.form.NeverSection142.$dirty && !$v.form.NeverSection142.required" class="mt-2 text-sm text-red-600">Please choose an answer.</p>

            <div v-if="form.NeverSection142 === 'Yes'" class="mt-3 pt-4 pb-1 border-l-4 border-gray-400 pl-4">
              <label for="NeverSanctionedDesc" class="block text-sm font-medium text-gray-700">
                Please provide further details.
              </label>
              <textarea
                v-model="form.NeverSection142Desc"
                class="mt-1 block w-full h-20 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400"
              />
              <p v-if="$v.form.NeverSection142Desc.$dirty && !$v.form.NeverSection142Desc.required" class="mt-2 text-sm text-red-600">Please provide some details.</p>
            </div>
          </div>



          <div class="flex flex-col items-center">
            <TailwindButton
              type="submit"
              size="xlarge"
              :loading="submitting"
              :disabled="submitting"
            >
              Submit
            </TailwindButton>
            <p v-if="$v.form.$dirty && $v.form.$anyError" class="mt-2 text-sm text-red-600">
              Please review validation errors.
            </p>
          </div>

        </form>
      </template>

    </main>
  </div>

  <tti-footer></tti-footer>

</div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import TailwindButton from '@/prototype/TailwindButton.vue'
import TailwindToggle from '@/prototype/TailwindToggle.vue'
import TailwindButtonGroup from './TailwindButtonGroup.vue'

import TutorService from '@/services/TutorServices'
const tutorService = new TutorService()

import ReferenceService from '@/services/ReferencesServices'
const referenceService = new ReferenceService()

export default {
  components: {
    'tti-header': () => import('./HeaderNew'),
    'tti-footer': () => import('./Footer'),
    TailwindButton,
    TailwindToggle,
    TailwindButtonGroup,
  },
  data() {
    return {
      loading: true,
      reference: null,
      tutor: null,
      notFound: false,
      form: {
        Id: null,
        RefereeName: null,
        Relationship: null,
        RelationshipLength: null,
        UnsuitableToTutor: null,
        UnsuitableToTutorDesc: null,
        SuitableWorkWithChildren: null,
        SuitableWorkWithChildrenDesc: null,
        NotInvolvedInExtremism: null,
        NotInvolvedInExtremismDesc: null,
        NeverSanctioned: null,
        NeverSanctionedDesc: null,
        NeverReferredToDfE: null,
        NeverReferredToDfEDesc: null,
        NeverSection142: null,
        NeverSection142Desc: null,
      },
      submitting: false,
      submitted: false,
    }
  },
  validations: {
    form: {
      RefereeName: { required },
      Relationship: { required },
      RelationshipLength: { required },
      UnsuitableToTutor: { required },
      UnsuitableToTutorDesc: { required: requiredIf(function() { return this.form.UnsuitableToTutor === 'Yes' }) },
      SuitableWorkWithChildren: { required },
      SuitableWorkWithChildrenDesc: { required: requiredIf(function() { return this.form.SuitableWorkWithChildren === 'Yes' }) },
      NotInvolvedInExtremism: { required },
      NotInvolvedInExtremismDesc: { required: requiredIf(function() { return this.form.NotInvolvedInExtremism === 'Yes' }) },
      NeverSanctioned: { required },
      NeverSanctionedDesc: { required: requiredIf(function() { return this.form.NeverSanctioned === 'Yes' }) },
      NeverReferredToDfE: { required },
      NeverReferredToDfEDesc: { required: requiredIf(function() { return this.form.NeverReferredToDfE === 'Yes' }) },
      NeverSection142: { required },
      NeverSection142Desc: { required: requiredIf(function() { return this.form.NeverSection142 === 'Yes' }) },
    }
  },
  async mounted() {
    try {
      this.reference = (await referenceService.get(this.$route.params.id)).data  
      this.tutor =  (await tutorService.get(this.reference.TutorId, true)).data
    } catch {
      this.notFound = true
      this.loading = false
      return
    }
    
    this.form.Id = this.reference.Id
    this.form.RefereeName = this.reference.Name
    this.loading = false
  },
  methods: {
    async submitForm(hasDeclined) {

      if (!hasDeclined) {
        this.$v.$touch()
        if (this.$v.$invalid) return
      }

      this.submitting = true

      try {
        await referenceService.submit(
          hasDeclined,
          this.form,
        )  
      } catch (error) {
        this.submitting = false
        alert('There has been a problem.')
        return
      }

      this.submitted = true
    }
  }
}
</script>

<style>

</style>